// LISTS: a sponsor's ask, the ranked list, Peter's review, and the file he hands over.
//
// Nothing is sent from this page and nothing is drafted. Claude is used for one thing, reading a pasted sponsor
// note into filters, and only when the Worker has an API key; rules do it otherwise. The ranking, holds and file
// rows come from shared/lists-engine.ts, the same code the Claude Code link (worker/mcp.ts) runs.
//
// Storage: asks at /api/lists/asks, one review per ask at /api/lists/:askId (keep or leave-off choices, people
// added by hand, the view the sponsor gets, and each download).

import { CAPITAL_TYPES, parseMoneyRange } from "../../shared/categorize";
import { SECTORS } from "../../shared/network";
import {
  ASK_REGIONS, VIEW_LABEL, buildList, clientRows, fileStem, guessAskFromText, keptPeople, keyOf, money, normalizeAsk, reviewRows,
  searchPeople, snapshot, snapshotRows, toCsv, toTable,
  type Ask, type ClientRow, type ClientView, type EngineEnv, type ListContact, type ListResult, type ReviewState, type Scored,
} from "../../shared/lists-engine";
import type { AppContext } from "../context";
import { buildXlsx } from "../lib/xlsx-write";
import { debounce, fmtDay, h, mount, num, type Child } from "../ui/dom";
import { iata } from "../ui/iata";
import { emptyState, key, panel, seg, sw, ticks } from "../ui/widgets";
import { STARTER_ASKS } from "./starter-asks";

type Tab = "list" | "held" | "near" | "file";
type Mode = "loading" | "ask" | "form" | "snapshot";

const VIEWS: ClientView[] = ["blind", "named", "full"];
const VIEW_SHORT: Record<ClientView, string> = { blind: "Profiles only", named: "Names", full: "Names + contact info" };
const XLSX = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
const PICKABLE_TYPES = [...CAPITAL_TYPES, "Operator / executive"];

/** Where Peter was, so leaving for a person's card and coming back lands on the same ask and tab. */
const memory: { selected: string | null; tab: Tab } = { selected: null, tab: "list" };

const plural = (n: number, one: string, many = one + "s") => `${num(n)} ${n === 1 ? one : many}`;

// Layout only; colors and type come from app.css.
const LAYOUT_CSS = `
.lists{display:grid;grid-template-columns:260px minmax(0,1fr);gap:16px;align-items:start}
.lists>*{min-width:0}
.lists .dlist+.dlist{margin-top:10px}
.lists .ask-text{max-width:80ch;margin:8px 0 10px}
.lists .gets{display:flex;align-items:center;gap:10px;flex-wrap:wrap;margin-top:12px}
.lists .tabs{margin:14px 0 10px}
.lists .tabhead{display:flex;align-items:center;gap:10px;flex-wrap:wrap;margin-bottom:10px}
.lists .tabhead .help{margin:0}
.lists .tabhead .grow{flex:1}
.lists td.keep{width:1%}
.lists tr.off td{opacity:.45}
.lists tr.off td.keep{opacity:1}
.lists td .nm a:hover{text-decoration:underline}
.lists .found{border:1px solid var(--line);margin-top:8px}
.lists .found>div{display:flex;align-items:center;gap:10px;padding:6px 10px;border-bottom:1px solid var(--line)}
.lists .found>div:last-child{border-bottom:0}
.lists .found .grow{flex:1;min-width:0}
.lists .grp>.micro{display:block;margin-bottom:4px}
.lists .add{margin-top:14px}
@media (max-width:1100px){.lists{grid-template-columns:minmax(0,1fr)}}
.lists td.fit{min-width:240px}
@media (max-width:760px){.lists .table-wrap table{min-width:760px}}
`;

function ensureStyles(doc: Document) {
  if (doc.querySelector("style[data-lists-layout]")) return;
  const s = doc.createElement("style");
  s.setAttribute("data-lists-layout", "");
  s.textContent = LAYOUT_CSS;
  doc.head.appendChild(s);
}

const th = (label: string, cls = "") => h("th", { scope: "col", class: cls || null }, label);
const group = (label: string, control: Child, cls = "") => h("div", { class: "grp " + cls }, h("span", { class: "micro" }, label), control);

/** A keep switch with a real label for screen readers. */
function keepSwitch(on: boolean, label: string, onChange: (v: boolean) => void): HTMLElement {
  const input = h("input", { type: "checkbox", checked: on, "aria-label": label });
  input.addEventListener("change", () => onChange(input.checked));
  return h("label", { class: "sw" }, input, h("span", { class: "trk" }));
}

/* ------------------------------------------------------------------ the page */

export function mountLists(el: HTMLElement, ctx: AppContext): () => void {
  ensureStyles(el.ownerDocument);

  let net = ctx.network;
  let contacts: ListContact[] = net?.contacts ?? [];
  let byKey: Map<string, ListContact> | null = null;
  const env = (): EngineEnv => ({ pastDeals: net?.past_deals ?? [], overlay: (k) => ctx.state.notes[k], today: ctx.asOf });

  let asks: Ask[] = [];
  let mode: Mode = "loading";
  let selected: string | null = memory.selected;
  let tab: Tab = memory.tab;
  let confirmDel = false;
  let search = "";
  let draft: Ask | null = null;
  let editingId: string | null = null;
  let filling = false;
  let loadStarted = false;
  let tabsEl: HTMLElement | null = null;
  const lists = new Map<string, ListResult>();
  const reviews = new Map<string, ReviewState>();
  const loaded = new Set<string>();
  const timers = new Map<string, ReturnType<typeof setTimeout>>();
  let sentCache: { askId: string; map: Map<string, string[]> } | null = null;

  const current = () => asks.find((a) => a.id === selected) ?? null;
  const listOf = (a: Ask) => {
    let L = lists.get(a.id);
    if (!L) {
      L = buildList(contacts, a, env());
      lists.set(a.id, L);
    }
    return L;
  };
  /** The saved review, created on first change. */
  const reviewOf = (a: Ask): ReviewState => {
    let r = reviews.get(a.id);
    if (!r) {
      r = { askId: a.id, decisions: {}, added: [] };
      reviews.set(a.id, r);
    }
    return r;
  };
  const kept = (a: Ask) => keptPeople(listOf(a), reviews.get(a.id) ?? null, contacts, env());
  const viewFor = (a: Ask): ClientView => reviews.get(a.id)?.view ?? a.view;
  const contactByKey = (k: string) => {
    if (!byKey) byKey = new Map(contacts.map((c) => [keyOf(c), c]));
    return byKey.get(k);
  };

  /* ---------------- persistence */

  async function loadReview(a: Ask) {
    if (loaded.has(a.id)) return;
    loaded.add(a.id);
    try {
      const r = await ctx.api.get<ReviewState>(`/api/lists/${encodeURIComponent(a.id)}`);
      if (r && typeof r === "object" && r.decisions) {
        // keep anything Peter changed while this was loading
        const mine = reviews.get(a.id);
        reviews.set(a.id, {
          askId: a.id,
          decisions: { ...r.decisions, ...(mine?.decisions ?? {}) },
          added: [...new Set([...(r.added ?? []), ...(mine?.added ?? [])])],
          view: mine?.view ?? r.view,
          updatedAt: mine?.updatedAt ?? r.updatedAt,
          exports: [...(r.exports ?? []), ...(mine?.exports ?? [])],
        });
      }
    } catch {
      loaded.delete(a.id); // offline: try again next time
    }
  }

  function saveReview(a: Ask, now = false) {
    const r = reviewOf(a);
    r.updatedAt = new Date().toISOString();
    clearTimeout(timers.get(a.id));
    const go = async () => {
      timers.delete(a.id);
      try {
        await ctx.api.put(`/api/lists/${encodeURIComponent(a.id)}`, r);
        const s = el.querySelector("[data-saved]");
        if (s) s.textContent = savedText(a);
      } catch {
        ctx.toast("Couldn't save your review. Check the connection and try again.", "err");
      }
    };
    if (now) void go();
    else timers.set(a.id, setTimeout(go, 600));
  }

  async function saveAsks(): Promise<boolean> {
    try {
      await ctx.api.put("/api/lists/asks", asks);
      return true;
    } catch {
      ctx.toast("Couldn't save the ask. Check the connection and try again.", "err");
      return false;
    }
  }

  const sortAsks = () => asks.sort((a, b) => (b.received || b.createdAt || "").localeCompare(a.received || a.createdAt || ""));

  async function load() {
    loadStarted = true;
    try {
      const got = await ctx.api.get<unknown>("/api/lists/asks");
      const raw = Array.isArray(got) ? got : [];
      asks = raw.filter((x): x is Partial<Ask> & { id: string } => !!x && typeof x === "object" && typeof (x as Ask).id === "string").map((x) => normalizeAsk(x));
    } catch {
      asks = [];
      ctx.toast("Couldn't load your asks. Check the connection.", "warn");
    }
    sortAsks();
    if (!asks.some((a) => a.id === selected)) selected = asks[0]?.id ?? null;
    if (mode === "loading") mode = "ask";
    await Promise.all(asks.map(loadReview));
    render();
  }

  /* ---------------- rendering */

  function render() {
    memory.selected = selected;
    memory.tab = tab;
    const head = h(
      "div",
      { class: "pagehead" },
      h("h1", { class: "title" }, "Lists"),
      net ? h("span", { class: "meta" }, h("b", null, num(net.contacts.length)), " PEOPLE · AS OF ", h("b", null, fmtDay(ctx.asOf, true))) : null,
      h("span", { class: "grow" }),
      net
        ? key("Network snapshot", () => {
            mode = mode === "snapshot" ? "ask" : "snapshot";
            confirmDel = false;
            render();
          }, { sm: true, on: mode === "snapshot", title: "Name-free counts you can show any sponsor" })
        : null,
    );
    if (!net) {
      mount(el, head, emptyState(() => ctx.navigate("sources")));
      return;
    }
    mount(el, head, h("div", { class: "lists" }, h("div", null, renderLeft()), h("div", null, renderMain())));
  }

  function statusOf(a: Ask): { bar: string; lamp: string; word: string; title: string } {
    const r = reviews.get(a.id);
    const last = r?.exports?.at(-1);
    if (last) return { bar: "green", lamp: "lit green", word: "FILE", title: `File downloaded ${fmtDay(last.at.slice(0, 10), true)}` };
    if (r?.updatedAt) return { bar: "amber", lamp: "lit amber", word: "REVIEW", title: "In review" };
    return { bar: "", lamp: "unlit", word: "NEW", title: "Not started" };
  }

  function renderLeft(): HTMLElement {
    const box = h(
      "div",
      { class: "dlist" },
      h("div", { class: "hd" }, h("span", { class: "micro" }, `ASKS  ${asks.length}`), key("New ask", () => openForm(null), { sm: true, primary: true })),
    );
    for (const a of asks) {
      const st = statusOf(a);
      const on = a.id === selected && mode === "ask";
      const row = h(
        "div",
        { class: "drow" + (on ? " sel" : ""), tabIndex: 0, role: "button", "aria-current": on ? "true" : null, title: st.title },
        h("div", { class: "b " + st.bar }),
        h("div", { class: "m" }, h("div", { class: "n" }, a.client), h("div", { class: "d" }, [a.received ? "RCVD " + fmtDay(a.received) : "", `${a.size} MAX`, VIEW_SHORT[viewFor(a)].toUpperCase()].filter(Boolean).join(" · "))),
        h("div", { class: "l" }, h("span", { class: "lamp " + st.lamp }, st.word)),
      );
      const open = () => void select(a.id);
      row.addEventListener("click", open);
      row.addEventListener("keydown", (e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          open();
        }
      });
      box.appendChild(row);
    }
    if (!asks.length) box.appendChild(h("div", { class: "hd muted mono", style: "border:0;font-size:12px" }, mode === "loading" ? "Loading…" : "No asks yet."));
    return box;
  }

  function renderMain(): Child {
    if (mode === "loading") return panel("Lists", h("p", { class: "muted mono" }, "Loading your asks…"));
    if (mode === "form" && draft) return renderForm(draft);
    if (mode === "snapshot") return renderSnapshot();
    const a = current();
    if (!a) {
      return panel("Start with an ask", [
        h("p", { class: "help" }, "When a sponsor asks who in your network might look at their deal, add it here. You get a ranked list, a review step, and a file in the format you choose. Nothing is sent from this page."),
        h("div", { class: "keys" }, key("New ask", () => openForm(null), { primary: true }), key("Add Valkyrie, LEA and the San Diego dinner", () => void seed())),
      ]);
    }
    return renderAsk(a);
  }

  function criteriaChips(a: Ask): HTMLElement {
    const chips: Child[] = [];
    a.sectors.forEach((s, i) => chips.push(h("span", { class: "chip" + (i === 0 ? " mine" : "") }, i === 0 && a.sectors.length > 1 ? `${s} (main)` : s)));
    for (const t of a.types) chips.push(h("span", { class: "chip" }, t));
    if (a.minCheck) chips.push(h("span", { class: "chip" }, `Checks ${money(a.minCheck)}+`));
    if (a.states.length) chips.push(h("span", { class: "chip" }, `${a.states.join(", ")} only`));
    else if (a.regions.length) chips.push(h("span", { class: "chip" }, `${a.regions.join(", ")} only`));
    if (!a.capitalOnly) chips.push(h("span", { class: "chip" }, "Anyone, not just check writers"));
    chips.push(h("span", { class: "chip" }, `Up to ${a.size}`));
    return h("div", { class: "chips" }, chips);
  }

  function savedText(a: Ask): string {
    const at = reviews.get(a.id)?.updatedAt;
    if (!at) return "NOT REVIEWED YET";
    const d = new Date(at);
    return `SAVED ${d.toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }).toUpperCase()}`;
  }

  function countKids(a: Ask, L: ListResult, k: Scored[]): Child[] {
    const cell = (n: number, label: string) => h("div", null, h("b", null, num(n)), label);
    return [
      cell(k.length, "IN THE FILE"),
      cell(L.recommended.length, "RANKED"),
      cell(L.held.length, "HELD BACK"),
      cell(L.near.length, "CLOSE MISSES"),
      cell(L.considered, a.states.length || a.regions.length ? "IN THE AREA" : "LOOKED AT"),
      h("div", { "data-saved": "" }, savedText(a)),
    ];
  }

  function paintCounts(a: Ask) {
    const k = kept(a);
    const box = el.querySelector("[data-counts]");
    if (box) mount(box, countKids(a, listOf(a), k));
    const fileTab = tabsEl?.querySelectorAll("button")[3];
    if (fileTab) fileTab.textContent = `Sponsor file (${k.length})`;
  }

  function renderAsk(a: Ask): Child {
    const L = listOf(a);
    const k = kept(a);
    const v = viewFor(a);
    const right = confirmDel
      ? h(
          "span",
          { class: "confirm" },
          "Delete this ask and its review?",
          key("Yes, delete", () => void removeAsk(a), { danger: true, sm: true }),
          key("No", () => {
            confirmDel = false;
            render();
          }, { sm: true }),
        )
      : h(
          "div",
          { class: "keys" },
          key("Edit", () => openForm(a), { sm: true }),
          key("Delete", () => {
            confirmDel = true;
            render();
          }, { danger: true, sm: true }),
        );
    const header = panel(
      a.received ? `Sponsor ask · received ${fmtDay(a.received, true)}` : "Sponsor ask",
      [
        h("h2", { class: "title" }, a.client),
        a.text ? h("p", { class: "ask-text" }, a.text) : null,
        criteriaChips(a),
        h(
          "div",
          { class: "gets" },
          h("span", { class: "micro" }, "The sponsor gets"),
          seg(VIEWS.map((x) => ({ value: x, label: VIEW_SHORT[x] })), v, (nv) => {
            reviewOf(a).view = nv;
            saveReview(a);
            render();
          }),
        ),
      ],
      right,
    );
    tabsEl = seg<Tab>(
      [
        { value: "list", label: `On the list (${L.recommended.length})` },
        { value: "held", label: `Held back (${L.held.length})` },
        { value: "near", label: `Close misses (${L.near.length})` },
        { value: "file", label: `Sponsor file (${k.length})` },
      ],
      tab,
      (t) => {
        tab = t;
        render();
      },
      "tabs",
    );
    tabsEl.setAttribute("aria-label", "List sections");
    const body = tab === "list" ? listTab(a, L) : tab === "held" ? heldTab(a, L) : tab === "near" ? nearTab(a, L) : fileTab(a, k);
    return [header, h("div", { class: "counts", "data-counts": "" }, countKids(a, L, k)), tabsEl, body];
  }

  /* ---------------- table cells */

  function nameCell(c: ListContact): HTMLElement {
    return h(
      "td",
      { class: "clip" },
      h("div", { class: "nm" }, h("a", { href: "#/people/" + encodeURIComponent(keyOf(c)), title: "Open " + c.n + " in People" }, c.n)),
      h("div", { class: "sub" }, [c.ti, c.co].filter(Boolean).join(" · ") || " "),
    );
  }
  function whereCell(c: ListContact): HTMLElement {
    const loc = iata(c.ci || "", c.st || "");
    return h("td", null, h("span", { class: "loc2", title: loc.label }, loc.code), h("div", { class: "sub" }, c.ci && c.st ? `${c.ci}, ${c.st}` : c.st || c.rg || ""));
  }
  const typeCell = (s: Scored) => h("td", { class: "hm" }, s.type === "Unknown" ? h("span", { class: "muted" }, "Unknown") : s.type);
  const bandCell = (s: Scored) => h("td", { class: "hm" }, s.band === "Unknown" ? h("span", { class: "muted" }, "—") : s.band);

  function fitCell(a: Ask, s: Scored): HTMLElement {
    const g = s.signals;
    const all = (xs: string[]) => xs.length === s.focus.length && xs.every((x) => s.focus.includes(x));
    const extra = [
      g.invested.length ? `Co-invested with you${all(g.invested) ? "" : ` in ${g.invested.join(", ")}`}` : g.investedCount ? `In ${g.investedCount === 1 ? "one" : g.investedCount} of your past deals` : "",
      g.asked.length ? `Asked you for ${all(g.asked) ? "this kind of deal" : `${g.asked.join(", ")} deals`}${g.askedWhen ? `, ${g.askedWhen}` : ""}` : "",
      g.checkFit ? "Check size fits" : "",
    ].filter(Boolean);
    const head = a.sectors.length ? s.focus.join(", ") : s.c.rg || "";
    return h("td", { class: "fit" }, h("div", null, head || h("span", { class: "muted" }, "—")), extra.length ? h("div", { class: "sub" }, extra.join(" · ")) : null);
  }

  /** People already in another sponsor's file, so Peter doesn't wear out the same names. */
  function sentFor(a: Ask): Map<string, string[]> {
    if (sentCache?.askId === a.id) return sentCache.map;
    const map = new Map<string, string[]>();
    for (const other of asks) {
      if (other.id === a.id) continue;
      const last = reviews.get(other.id)?.exports?.at(-1);
      if (!last?.keys) continue;
      const label = `In ${other.client.replace(/\s*\(.*\)\s*$/, "")} file · ${fmtDay(last.at.slice(0, 10))}`;
      for (const k of last.keys) map.set(k, [...(map.get(k) ?? []), label]);
    }
    sentCache = { askId: a.id, map };
    return map;
  }

  function privateChips(a: Ask, s: Scored): HTMLElement {
    const box = h("div", { class: "chips" });
    for (const p of s.peterOnly) {
      if (p.startsWith("Your note: ")) box.appendChild(h("span", { class: "chip mine", title: p.slice(11) }, "Your note"));
      else box.appendChild(h("span", { class: "chip warn" }, p));
    }
    for (const x of sentFor(a).get(s.key) ?? []) box.appendChild(h("span", { class: "chip" }, x));
    return box;
  }

  /* ---------------- tabs */

  function listTab(a: Ask, L: ListResult): Child {
    if (!L.recommended.length)
      return [h("p", { class: "help" }, "No one passes every filter for this ask. Look at the close misses, or edit the ask to widen it."), addPanel(a)];
    const r = reviews.get(a.id);
    const rows = L.recommended.map((s, i) => {
      const off = r?.decisions[s.key] === "N";
      const tr: HTMLElement = h("tr", { class: off ? "off" : null });
      tr.append(
        h("td", { class: "keep" }, keepSwitch(!off, `Keep ${s.c.n} on the list`, (on) => toggleKeep(a, s.key, on, tr))),
        h("td", { class: "num" }, String(i + 1)),
        nameCell(s.c),
        typeCell(s),
        bandCell(s),
        whereCell(s.c),
        h("td", null, ticks(s.c.rs || 0)),
        fitCell(a, s),
        h("td", null, privateChips(a, s)),
      );
      return tr;
    });
    return [
      h(
        "div",
        { class: "tabhead" },
        h("p", { class: "help" }, "Switch off anyone you don't want to hand over. They stay out of the file. The last column is for you only."),
        h("span", { class: "grow" }),
        key("My review sheet", () => downloadReview(a), { sm: true, title: "Everyone considered, with your private reasons. For you, never for a sponsor." }),
      ),
      h(
        "div",
        { class: "table-wrap" },
        h("table", null, h("thead", null, h("tr", null, th("Keep"), th("#", "num"), th("Name"), th("Type", "hm"), th("Check", "hm"), th("Where"), th("Str"), th("Fit"), th("Only you see"))), h("tbody", null, rows)),
      ),
      addPanel(a),
    ];
  }

  function addPanel(a: Ask): HTMLElement {
    const r = reviews.get(a.id);
    const input = h("input", { type: "search", placeholder: "Name, firm or city", value: search, "aria-label": "Find a person to add" });
    const found = h("div", { class: "found" });
    const paint = () => {
      mount(found, foundRows(a));
      found.classList.toggle("hidden", !found.childNodes.length);
    };
    input.addEventListener(
      "input",
      debounce(() => {
        search = input.value;
        paint();
      }, 120),
    );
    paint();
    const keptKeys = new Set(kept(a).map((s) => s.key));
    const added = (r?.added ?? []).map((k) => contactByKey(k)).filter((c): c is ListContact => !!c);
    return panel(
      "Add someone the list missed",
      [
        h("div", { class: "filters", style: "margin-bottom:0" }, h("div", { class: "search" }, input)),
        found,
        added.length
          ? h(
              "div",
              { class: "table-wrap", style: "margin-top:10px" },
              h(
                "table",
                null,
                h("thead", null, h("tr", null, th("Added by you"), th("Status"), th(""))),
                h(
                  "tbody",
                  null,
                  added.map((c) => {
                    const k = keyOf(c);
                    return h(
                      "tr",
                      null,
                      nameCell(c),
                      h(
                        "td",
                        null,
                        keptKeys.has(k)
                          ? h("span", { class: "chip ok" }, "In the file")
                          : h("span", { class: "keys" }, h("span", { class: "chip hold" }, "Has a private hold"), key("Include anyway", () => decide(a, k), { sm: true })),
                      ),
                      h("td", { class: "num" }, key("Remove", () => removeAdded(a, k), { sm: true })),
                    );
                  }),
                ),
              ),
            )
          : null,
      ],
      null,
      "add",
    );
  }

  function foundRows(a: Ask): Child[] {
    if (search.trim().length < 2) return [];
    const onList = new Set([...listOf(a).recommended.map((s) => s.key), ...(reviews.get(a.id)?.added ?? [])]);
    const hits = searchPeople(contacts, search, 8);
    if (!hits.length) return [h("div", null, h("span", { class: "muted mono" }, `No one matches "${search.trim()}".`))];
    return hits.map((c) => {
      const k = keyOf(c);
      const meta = [c.ti, c.co, c.ci && c.st ? `${c.ci}, ${c.st}` : c.st].filter(Boolean).join(" · ");
      return h("div", null, h("span", { class: "grow" }, h("b", null, c.n), " ", h("span", { class: "muted" }, meta)), onList.has(k) ? h("span", { class: "micro" }, "On the list") : key("Add", () => addPerson(a, k), { sm: true }));
    });
  }

  function heldTab(a: Ask, L: ListResult): Child {
    if (!L.held.length) return h("p", { class: "help" }, "No one who fits this ask has a private hold.");
    const d = reviews.get(a.id)?.decisions ?? {};
    return [
      h("p", { class: "help" }, "These people fit the ask, but there's a reason to leave them off: a life event, a recent check in this sector, a bad experience, or a hold you set on People. They are never in a sponsor file unless you include them here."),
      h(
        "div",
        { class: "table-wrap" },
        h(
          "table",
          null,
          h("thead", null, h("tr", null, th("Name"), th("Type", "hm"), th("Why they're held back (private)"), th(""))),
          h(
            "tbody",
            null,
            L.held.map((s) => {
              const inc = d[s.key] === "Y";
              return h(
                "tr",
                null,
                nameCell(s.c),
                typeCell(s),
                h("td", null, h("div", { class: "chips" }, s.heldFor.map((x) => h("span", { class: "chip hold" }, x)))),
                h("td", { class: "num" }, key(inc ? "Included · undo" : "Include anyway", () => decide(a, s.key), { sm: true, on: inc })),
              );
            }),
          ),
        ),
      ),
    ];
  }

  function nearTab(a: Ask, L: ListResult): Child {
    if (!L.near.length) return h("p", { class: "help" }, "No close relationships miss this ask by just one thing.");
    const d = reviews.get(a.id)?.decisions ?? {};
    return [
      h("p", { class: "help" }, "People you're close to (strength 4 or 5) who miss this ask on one point. Add anyone you'd stretch for."),
      h(
        "div",
        { class: "table-wrap" },
        h(
          "table",
          null,
          h("thead", null, h("tr", null, th("Name"), th("Type", "hm"), th("Misses"), th("Str"), th("Fit"), th(""))),
          h(
            "tbody",
            null,
            L.near.map((s) => {
              const inc = d[s.key] === "Y";
              return h(
                "tr",
                null,
                nameCell(s.c),
                typeCell(s),
                h("td", null, h("div", { class: "chips" }, s.misses.map((m) => h("span", { class: "chip warn" }, m)))),
                h("td", null, ticks(s.c.rs || 0)),
                fitCell(a, s),
                h("td", { class: "num" }, key(inc ? "Added · undo" : "Add to list", () => decide(a, s.key), { sm: true, on: inc })),
              );
            }),
          ),
        ),
      ),
    ];
  }

  function rowsTable(rows: ClientRow[]): HTMLElement {
    const cols = rows.length ? Object.keys(rows[0]) : [];
    const numeric = (c: string) => c === "#" || typeof rows[0]?.[c] === "number";
    return h(
      "div",
      { class: "table-wrap" },
      h(
        "table",
        null,
        h("thead", null, h("tr", null, cols.map((c) => th(c, numeric(c) ? "num" : "")))),
        h("tbody", null, rows.map((row) => h("tr", null, cols.map((c) => h("td", { class: numeric(c) ? "num" : null }, String(row[c] ?? "")))))),
      ),
    );
  }

  function fileTab(a: Ask, k: Scored[]): Child {
    const v = viewFor(a);
    const rows = clientRows(a, k, v);
    const what =
      v === "blind"
        ? "No names or contact details. Each person has a code, so the sponsor asks you for an introduction by code."
        : v === "named"
          ? "Names, firms and titles. No email or phone."
          : "Names with email and phone, for an invitation list.";
    return [
      h(
        "div",
        { class: "tabhead" },
        h("p", { class: "help" }, h("b", null, VIEW_LABEL[v] + ". "), what, ` ${plural(rows.length, "person", "people")}. Your notes and hold reasons are never in this file.`),
        h("span", { class: "grow" }),
        h("div", { class: "keys" }, key("Download Excel", () => downloadFile(a, "xlsx"), { primary: true, sm: true, disabled: !rows.length }), key("CSV", () => downloadFile(a, "csv"), { sm: true, disabled: !rows.length })),
      ),
      rows.length ? rowsTable(rows) : h("p", { class: "muted mono" }, "The file is empty. Keep at least one person on the list."),
    ];
  }

  function renderSnapshot(): Child {
    const s = snapshot(contacts, env());
    const rows = snapshotRows(s);
    return panel(
      "Network snapshot · no names",
      [
        h("p", { class: "help" }, `${plural(s.checkWriters, "check writer")} across ${plural(s.rows.length, "sector")}. People on any private hold are left out (${num(s.onHold)}). No names, so you can show it to any sponsor.`),
        rows.length ? rowsTable(rows) : h("p", { class: "muted mono" }, "No check writers yet."),
      ],
      h(
        "div",
        { class: "keys" },
        key("Download Excel", () => ctx.save(`network-snapshot-${ctx.asOf}.xlsx`, new Blob([buildXlsx([{ name: "Snapshot", rows: toTable(rows) }]) as BlobPart], { type: XLSX })), { primary: true, sm: true }),
        key("CSV", () => ctx.save(`network-snapshot-${ctx.asOf}.csv`, new Blob([toCsv(rows)], { type: "text/csv;charset=utf-8" })), { sm: true }),
      ),
    );
  }

  /* ---------------- the ask form */

  function renderForm(d: Ask): HTMLElement {
    const text = (k: "client" | "received", type = "text", placeholder = "") => {
      const i = h("input", { type, value: d[k] ?? "", placeholder });
      i.addEventListener("input", () => (d[k] = i.value));
      return i;
    };
    const note = h("textarea", { rows: 4, placeholder: "Paste the sponsor's note, or describe the deal in a sentence or two" });
    note.value = d.text;
    note.addEventListener("input", () => (d.text = note.value));
    const main = h("select", null, h("option", { value: "" }, "None (a list by place, not by deal)"), SECTORS.map((s) => h("option", { value: s, selected: d.sectors[0] === s }, s)));
    main.addEventListener("change", () => {
      d.sectors = [main.value, ...d.sectors.slice(1).filter((s) => s !== main.value)].filter(Boolean);
      paintOthers();
    });
    const minCheck = h("input", { type: "text", value: d.minCheck ? money(d.minCheck) : "", placeholder: "$250K" });
    minCheck.addEventListener("change", () => (d.minCheck = parseMoneyRange(minCheck.value)[0]));
    const size = h("input", { type: "number", min: 1, max: 1000, value: String(d.size) });
    size.addEventListener("input", () => (d.size = Math.max(1, Math.min(1000, Number(size.value) || 40))));
    const states = h("input", { type: "text", value: d.states.join(", "), placeholder: "CA, AZ" });
    states.addEventListener("input", () => (d.states = states.value.split(/[\s,;]+/).map((s) => s.trim().toUpperCase()).filter((s) => /^[A-Z]{2}$/.test(s))));

    const toggles = (options: readonly string[], on: (x: string) => boolean, flip: (x: string) => void) => {
      const box = h("div", { class: "togs" });
      for (const x of options) {
        const b = key(x, () => {
          flip(x);
          b.classList.toggle("on", on(x));
          b.setAttribute("aria-pressed", String(on(x)));
        }, { sm: true, on: on(x) });
        box.appendChild(b);
      }
      return box;
    };
    const flipIn = (arr: string[], x: string) => {
      const i = arr.indexOf(x);
      if (i >= 0) arr.splice(i, 1);
      else arr.push(x);
    };
    const others = h("div");
    const paintOthers = () =>
      mount(
        others,
        toggles(
          SECTORS.filter((s) => s !== d.sectors[0]),
          (x) => d.sectors.slice(1).includes(x),
          (x) => {
            const rest = d.sectors.slice(1);
            flipIn(rest, x);
            d.sectors = [d.sectors[0], ...rest].filter(Boolean);
            if (!d.sectors[0] && rest.length) d.sectors = rest; // no main yet: the first pick leads
          },
        ),
      );
    paintOthers();
    const viewBox = h("div");
    const paintView = () => mount(viewBox, seg(VIEWS.map((x) => ({ value: x, label: VIEW_LABEL[x] })), d.view, (x) => ((d.view = x), paintView())));
    paintView();

    const fillBtn = key(filling ? "Reading…" : ctx.ai ? "Read the note with Claude" : "Fill in from the note", () => void fill(d), { sm: true, disabled: filling });
    const isNew = !editingId;
    return panel(isNew ? "New ask" : "Edit ask", [
      h(
        "div",
        { class: "formgrid" },
        group("Who's asking", text("client", "text", "Valkyrie (Austin, TX)")),
        group("Received", text("received", "date")),
        group("What they want", note, "wide"),
        h("div", { class: "wide keys" }, fillBtn, h("span", { class: "help" }, "Sets the fields below from the note. Check them before saving.")),
        group("Main sector", main),
        group("Smallest check", minCheck),
        group("List size", size),
        group("Only these states", states),
        group("Other sectors that count", others, "wide"),
        group("Investor types they want (none = any check writer)", toggles(PICKABLE_TYPES, (x) => d.types.includes(x), (x) => flipIn(d.types, x)), "wide"),
        group("Only these regions", toggles(ASK_REGIONS, (x) => d.regions.includes(x), (x) => flipIn(d.regions, x)), "wide"),
        h("div", { class: "wide" }, sw("Only people who write checks", d.capitalOnly, (v) => (d.capitalOnly = v))),
        group("The sponsor gets", viewBox, "wide"),
      ),
      h("div", { class: "sep" }),
      h("div", { class: "keys" }, key(isNew ? "Save and build the list" : "Save changes", () => void saveForm(d), { primary: true }), key("Cancel", () => closeForm())),
    ]);
  }

  function openForm(a: Ask | null) {
    editingId = a?.id ?? null;
    draft = a
      ? { ...a, sectors: [...a.sectors], types: [...a.types], regions: [...a.regions], states: [...a.states], view: viewFor(a) }
      : normalizeAsk({ id: "draft", client: "", received: ctx.asOf, sectors: [], types: [], view: "blind" });
    if (!a) draft.client = "";
    mode = "form";
    confirmDel = false;
    render();
  }
  function closeForm() {
    draft = null;
    editingId = null;
    mode = "ask";
    render();
  }

  async function fill(d: Ask) {
    if (!d.text.trim()) {
      ctx.toast("Paste the sponsor's note first.", "warn");
      return;
    }
    filling = true;
    render();
    let guess: Partial<Ask> = guessAskFromText(d.text);
    let byClaude = false;
    if (ctx.ai) {
      try {
        const out = await ctx.ai<ParsedAsk>("parse-ask", { system: PARSE_SYSTEM, prompt: `Sponsor note:\n"""\n${d.text.slice(0, 6000)}\n"""`, schema: PARSE_SCHEMA, effort: "low" });
        if (out && Array.isArray(out.sectors)) {
          guess = {
            client: out.client || undefined,
            sectors: out.sectors,
            types: out.types,
            minCheck: out.min_check || null,
            regions: out.regions,
            states: out.states,
            size: out.size || undefined,
            view: out.view,
          };
          byClaude = true;
        }
      } catch {
        ctx.toast("Claude couldn't read it, so the rules filled it in instead.", "warn");
      }
    }
    if (draft !== d) return; // Peter left the form while Claude was reading
    const merged = normalizeAsk({
      ...d,
      ...Object.fromEntries(Object.entries(guess).filter(([, v]) => v !== undefined)),
      id: d.id,
      text: d.text,
      client: d.client || guess.client || "",
      received: d.received,
    });
    draft = { ...merged, client: d.client || guess.client || "" };
    filling = false;
    render();
    ctx.toast(byClaude ? "Claude read the note. Check the fields before saving." : "Filled in from the note. Check the fields before saving.");
  }

  async function saveForm(d: Ask) {
    if (!d.client.trim()) {
      ctx.toast("Add who's asking.", "warn");
      return;
    }
    if (!d.sectors.length && !d.types.length && !d.regions.length && !d.states.length) {
      ctx.toast("Pick at least a sector, an investor type, a region or a state.", "warn");
      return;
    }
    if (editingId) {
      const i = asks.findIndex((x) => x.id === editingId);
      if (i >= 0) asks[i] = normalizeAsk({ ...asks[i], ...d, id: editingId });
      lists.delete(editingId);
      const r = reviews.get(editingId);
      if (r && r.view !== d.view) {
        r.view = d.view;
        saveReview(asks[i], true);
      }
      selected = editingId;
    } else {
      const id = `A-${Date.now().toString(36).toUpperCase()}`;
      asks.push(normalizeAsk({ ...d, id, createdAt: new Date().toISOString() }));
      selected = id;
    }
    sortAsks();
    draft = null;
    editingId = null;
    mode = "ask";
    tab = "list";
    sentCache = null;
    if (await saveAsks()) ctx.toast("Saved. The list is built from your network as of today.");
    render();
  }

  /* ---------------- actions */

  async function select(id: string) {
    selected = id;
    mode = "ask";
    tab = "list";
    confirmDel = false;
    search = "";
    sentCache = null;
    const a = current();
    if (a) await loadReview(a);
    render();
  }

  async function seed() {
    const have = new Set(asks.map((x) => x.id));
    asks.push(...STARTER_ASKS.filter((x) => !have.has(x.id)).map((x) => ({ ...x, createdAt: new Date().toISOString() })));
    sortAsks();
    await saveAsks();
    await select(asks[0].id);
  }

  async function removeAsk(a: Ask) {
    asks = asks.filter((x) => x.id !== a.id);
    lists.delete(a.id);
    reviews.delete(a.id);
    clearTimeout(timers.get(a.id));
    confirmDel = false;
    sentCache = null;
    selected = asks[0]?.id ?? null;
    await saveAsks();
    render();
  }

  function toggleKeep(a: Ask, k: string, on: boolean, tr: HTMLElement) {
    const r = reviewOf(a);
    if (on) delete r.decisions[k];
    else r.decisions[k] = "N";
    tr.classList.toggle("off", !on);
    paintCounts(a);
    saveReview(a);
  }

  /** Include someone held back, add a close miss, or undo either. */
  function decide(a: Ask, k: string) {
    const r = reviewOf(a);
    if (r.decisions[k] === "Y") delete r.decisions[k];
    else r.decisions[k] = "Y";
    saveReview(a);
    render();
  }

  function addPerson(a: Ask, k: string) {
    const r = reviewOf(a);
    if (!r.added.includes(k)) r.added.push(k);
    saveReview(a);
    render();
    el.querySelector<HTMLInputElement>('input[type="search"]')?.focus();
  }

  function removeAdded(a: Ask, k: string) {
    const r = reviewOf(a);
    r.added = r.added.filter((x) => x !== k);
    if (r.decisions[k] === "Y") delete r.decisions[k];
    saveReview(a);
    render();
  }

  function downloadFile(a: Ask, fmt: "xlsx" | "csv") {
    const v = viewFor(a);
    const k = kept(a);
    const rows = clientRows(a, k, v);
    const stem = `${fileStem(a, ctx.asOf)}-${v === "blind" ? "profiles" : v === "named" ? "names" : "contacts"}`;
    if (fmt === "xlsx") ctx.save(stem + ".xlsx", new Blob([buildXlsx([{ name: "Sponsor file", rows: toTable(rows) }]) as BlobPart], { type: XLSX }));
    else ctx.save(stem + ".csv", new Blob([toCsv(rows)], { type: "text/csv;charset=utf-8" }));
    const r = reviewOf(a);
    (r.exports ??= []).push({ at: new Date().toISOString(), view: v, count: k.length, keys: k.map((s) => s.key) });
    sentCache = null;
    saveReview(a, true);
    ctx.toast(`${plural(k.length, "person", "people")} in the file. Nothing was sent; it's in your downloads.`);
    render();
  }

  function downloadReview(a: Ask) {
    const rows = reviewRows(listOf(a), reviews.get(a.id) ?? null);
    ctx.save(`${fileStem(a, ctx.asOf)}-PRIVATE-review.xlsx`, new Blob([buildXlsx([{ name: "My review (private)", rows: toTable(rows) }]) as BlobPart], { type: XLSX }));
  }

  /* ---------------- lifecycle */

  let off: (() => void) | null = null;
  const stop = () => {
    off?.();
    off = null;
    for (const t of timers.values()) clearTimeout(t);
  };
  off = ctx.onChange(() => {
    if (!el.isConnected) return stop();
    if (ctx.network !== net) {
      net = ctx.network;
      contacts = net?.contacts ?? [];
      byKey = null;
    }
    // notes and holds may have changed on another page: rebuild lists on the next look
    lists.clear();
    sentCache = null;
    if (net && !loadStarted) void load();
    if (mode !== "form") render();
  });

  render();
  if (net) void load();
  return () => {
    // flush any review still waiting to save
    for (const [id, t] of timers) {
      clearTimeout(t);
      const a = asks.find((x) => x.id === id);
      const r = reviews.get(id);
      if (a && r) void ctx.api.put(`/api/lists/${encodeURIComponent(id)}`, r).catch(() => undefined);
    }
    timers.clear();
    off?.();
    off = null;
  };
}

/* ------------------------------------------------------------------ reading a sponsor note (Claude, optional) */

interface ParsedAsk {
  client: string;
  sectors: string[];
  types: string[];
  min_check: number;
  regions: string[];
  states: string[];
  size: number;
  view: ClientView;
}

const PARSE_SYSTEM = [
  "You read a note a sponsor sent to a capital raiser, asking who in his network might fit their deal or event.",
  "Return the filters for building that list, using only the allowed values. Use only what the note says.",
  "sectors: the sectors the deal is in, main one first; empty for a list by place only.",
  "types: investor types only if the sponsor names them; otherwise empty.",
  "min_check: the smallest check in US dollars if stated, otherwise 0.",
  "regions and states: only if the sponsor limits geography (states as two-letter codes).",
  "size: how many names they want if stated, otherwise 0.",
  "view: blind for a deal where the raiser makes the introductions, named when they ask for a list of names, full when they need contact details to send invitations.",
  "client: who is asking, if the note says; otherwise empty.",
].join("\n");

const PARSE_SCHEMA = {
  type: "object",
  additionalProperties: false,
  required: ["client", "sectors", "types", "min_check", "regions", "states", "size", "view"],
  properties: {
    client: { type: "string" },
    sectors: { type: "array", items: { type: "string", enum: SECTORS } },
    types: { type: "array", items: { type: "string", enum: PICKABLE_TYPES } },
    min_check: { type: "number" },
    regions: { type: "array", items: { type: "string", enum: ASK_REGIONS } },
    states: { type: "array", items: { type: "string" } },
    size: { type: "integer" },
    view: { type: "string", enum: VIEWS },
  },
};
